import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { loadRemoteEntry, loadRemoteModule } from '@angular-architects/module-federation';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { addIconResolver, InjectTranslationsService, REMOTE_MODULE_LOADER, RemoteModuleLoader } from '@celum/shared/util';

import { NotificationClientConfig } from '../model/notification-client-config.model';
import { NotificationItemsConfig } from '../model/notification-items-config.model';

/** Default for hosts built with webpack + ModuleFederationPlugin */
const webpackRemoteModuleLoader: RemoteModuleLoader = {
  loadRemoteEntry: (remoteEntryUrl, remoteName) => loadRemoteEntry(remoteEntryUrl, remoteName),
  loadRemoteModule: (remoteName, exposedModule) => loadRemoteModule({ remoteName, exposedModule })
};

@Injectable({ providedIn: 'root' })
export class NotificationComponentsLoaderService {
  private httpClient = inject(HttpClient);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private injectTranslationsService = inject(InjectTranslationsService);
  private remoteModuleLoader = inject(REMOTE_MODULE_LOADER, { optional: true }) ?? webpackRemoteModuleLoader;
  private loadedConfigurationMap = new Map<string, NotificationItemsConfig>();

  public loadNotificationComponents(clientConfig: NotificationClientConfig): Observable<NotificationItemsConfig> {
    const baseSourcesUrl = clientConfig.value.baseSourcesUrl;
    const itemsConfigUrl = baseSourcesUrl + clientConfig.value.notificationItemsConfigurationPath;

    if (this.loadedConfigurationMap.has(baseSourcesUrl)) {
      return of(this.loadedConfigurationMap.get(baseSourcesUrl));
    }

    return this.httpClient.get<NotificationItemsConfig>(itemsConfigUrl).pipe(
      switchMap(itemsConfig => this.loadAngularElements(baseSourcesUrl, itemsConfig).then(() => itemsConfig)),
      switchMap(itemsConfig => this.bootstrapAngularElements(itemsConfig).then(() => itemsConfig)),
      tap(config => this.loadedConfigurationMap.set(baseSourcesUrl, config)),
      tap({ error: err => console.error(`NotificationComponentsLoaderService: Error loading notification components:`, err) })
    );
  }

  private async loadAngularElements(baseSourcesUrl: string, itemsConfig: NotificationItemsConfig): Promise<void> {
    const remoteEntryUrl = baseSourcesUrl + itemsConfig.path;

    return this.remoteModuleLoader
      .loadRemoteEntry(remoteEntryUrl, itemsConfig.remoteName)
      .then(async () => {
        await this.injectTranslationsService.loadTranslations({ basePath: baseSourcesUrl, ...itemsConfig });
        return addIconResolver({ basePath: baseSourcesUrl, icons: itemsConfig.icons }, this.iconRegistry, this.sanitizer);
      })
      .catch(error => {
        console.error(`NotificationComponentsLoaderService: Error loading angular components for ${itemsConfig.name}`, error);
        return Promise.reject(error);
      });
  }

  private async bootstrapAngularElements(itemsConfig: NotificationItemsConfig): Promise<void> {
    return this.remoteModuleLoader.loadRemoteModule(itemsConfig.remoteName, itemsConfig.exposedModule).then((): void => undefined);
  }
}
